import { inject, type ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '@jet-app/music-classical/dependencies';
import type { Opt } from '@jet/environment';
import { getBaseSeoData } from './BaseSeoData';
import type { AlbumSchema, CropCode, SeoData, TrackRecordSEO } from './types';
import type { AlbumPage } from '@jet-app/music-classical/models/pages/album';
import {
    createActionSchema,
    ACTION_SCHEMA_TYPE,
    getTracksForSEO,
    schemaDataForTrack,
    getTrackOpenGraphData,
    schemaDataForArtist,
} from '@jet-app/music-classical/utilities/seo/utils';
import { buildSrc } from '@jet-app/music-classical/utilities/seo/utils';
import { formatDuration } from './duration';
import type { TrackLockupCollection } from '@jet-app/music-classical/models/section/track-lockup/TrackLockupCollection';

/**
 * @method getAlbumSeoData
 * @param {string} url Album canonical URL
 * @param {AlbumPage} page album page object
 * @param {ObjectGraph} objectGraph
 * @returns {SeoData}
 */
export function getAlbumSeoData(
    url: string,
    page: AlbumPage,
    objectGraph: ObjectGraph,
): SeoData {
    const localization = inject(ObjectGraphType.localization, objectGraph);
    const baseSeoData: SeoData = getBaseSeoData(url, objectGraph);
    const albumId: string = page.header.id;
    const albumName: string = page.header.title;
    const artistName: string =
        page.header.subtitle || page.header.subtitleLinks?.[0]?.title || '';
    const year: string = page.header.year;
    const numTracks: number = page.header.trackCount;
    const totalDurationSeconds: number = page.header.totalDurationMs / 1000;

    // Ensure we are only working with track lockups.  There may be others,
    // such as footers, etc. that we won't need to generate SEO from.
    const trackLockups: TrackLockupCollection[] = page.sections?.filter(
        (section) => section.itemKind === 'trackLockup',
    );

    const tracks: TrackRecordSEO[] = getTracksForSEO(trackLockups);

    function getPageTitle() {
        if (albumName && artistName) {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.Meta.Album.Title',
                { albumName, artistName },
            );
        }

        return baseSeoData.pageTitle;
    }

    function getPageDescription() {
        if (
            albumName &&
            artistName &&
            year &&
            numTracks &&
            totalDurationSeconds
        ) {
            return localization.string(
                'ASE.Web.MusicClassicalClientJS.Meta.Album.Description',
                {
                    albumName,
                    artistName,
                    year,
                    numTracks,
                    duration: formatDuration(totalDurationSeconds, objectGraph),
                },
            );
        }

        return baseSeoData.description;
    }

    const pageTitle: string = getPageTitle();
    const description: Opt<string> = getPageDescription();
    const artworkUrl: Opt<string> = page.header.artwork?.template;
    const crop: CropCode = 'bb';

    function getAlbumSchemaContent(): AlbumSchema {
        const schema = {
            '@context': 'http://schema.org',
            '@type': 'MusicAlbum',
            description,
            name: albumName,
            potentialAction: createActionSchema(url, ACTION_SCHEMA_TYPE.Listen),
            track: tracks?.map((track) => schemaDataForTrack(track)),
            url,
            image: artworkUrl
                ? buildSrc(
                      artworkUrl,
                      {
                          width: 486,
                          height: 486,
                          crop: 'bb',
                          fileType: baseSeoData.fileType,
                      },
                      {},
                  )
                : null,
        };

        if (page.header.subtitleLinks?.length) {
            schema.byArtist = page.header.subtitleLinks.map((action) =>
                schemaDataForArtist(action),
            );
        } else if (page.header.subtitle) {
            schema.byArtist = schemaDataForArtist(page.header.subtitle);
        }

        return schema;
    }

    /**
     *
     * Generate SEO data object
     *
     */
    const seoData = {
        ...baseSeoData,
        pageTitle,
        description,

        /**
         * Social OG Tags
         */
        socialTitle: pageTitle,
        socialDescription: description,
        ogSongs: tracks?.map((track) => getTrackOpenGraphData(track)),
        ogType: 'music.album',
        songsCount: numTracks,

        /**
         *
         * Apple-specific Values
         *
         */
        appleContentId: albumId,
        appleTitle: pageTitle,
        appleDescription: description,

        /**
         *
         * Artwork Values
         *
         */
        crop,
        twitterCropCode: crop,
        artworkUrl: artworkUrl ?? baseSeoData.artworkUrl,

        /**
         *
         * Structured Data/schema.org Values
         *
         */
        schemaName: 'schema:music-album',
        schemaContent: getAlbumSchemaContent(),
    };

    return seoData;
}

export function addingAlbumSeoData(
    url: string,
    page: AlbumPage,
    objectGraph: ObjectGraph,
): AlbumPage {
    return {
        ...page,
        seoData: getAlbumSeoData(url, page, objectGraph),
    };
}
